import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { normalizeMarkdownContent } from "./MarkdownPreview";

interface Props {
  value: string;
  onChange: (nextValue: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
}

export function MarkdownField({
  value,
  onChange,
  placeholder = "Enter Markdown...",
  rows = 4,
  disabled = false,
  className = "",
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    const textarea = event.currentTarget;
    const selectionStart = textarea.selectionStart;
    const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
    const lineEnd = value.indexOf("\n", selectionStart);
    const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd);
    const bulletMatch = currentLine.match(/^(\s*[-*]\s+)/);
    if (!bulletMatch) return;

    event.preventDefault();
    const prefix = bulletMatch[1];
    const insertion = `\n${prefix}`;
    const nextValue = `${value.slice(0, selectionStart)}${insertion}${value.slice(selectionStart)}`;
    onChange(nextValue);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.selectionStart = textareaRef.current.selectionEnd = selectionStart + insertion.length;
      }
    });
  };

  if (isEditing && !disabled) {
    return (
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setIsEditing(false)}
        onKeyDown={handleKeyDown}
        rows={rows}
        className={`w-full px-3 py-2 text-[10pt] border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none disabled:bg-slate-50 disabled:text-slate-500 ${className}`.trim()}
        placeholder={placeholder}
      />
    );
  }

  const normalized = normalizeMarkdownContent(value);
  const hasContent = normalized.trim().length > 0;

  return (
    <div
      onClick={() => {
        if (!disabled) setIsEditing(true);
      }}
      className={`w-full min-h-[${rows * 28}px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-[10pt] ${disabled ? "bg-slate-50 text-slate-500" : "cursor-text"} ${className}`.trim()}
      style={{ minHeight: `${rows * 28}px` }}
    >
      {hasContent ? (
        <div className="markdown-body max-w-none text-xs text-slate-800">
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
              ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-1">{children}</ul>,
              ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-1">{children}</ol>,
              li: ({ children }) => <li className="leading-relaxed">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
              a: ({ children, href }) => (
                <a href={href} className="text-indigo-600 underline underline-offset-2">
                  {children}
                </a>
              ),
            }}
          >
            {normalized}
          </ReactMarkdown>
        </div>
      ) : (
        <span className="text-slate-400">{placeholder}</span>
      )}
    </div>
  );
}
